/**
 * screens/shell/chrome.tsx
 * Persistent shell chrome — TopBar, SubNav, SectionSelect, PermissionDenied,
 * SessionReauthModal. Faithful RN port of design/shell.jsx.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ScrollView,
  Modal as RNModal,
  TextInput,
  Platform,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useShellNav, NAV, ADMIN_NAV } from '../../navigation/shellNav';
import Icon, { IconName } from '../../components/icon';
import Avatar from '../../components/avatar';
import { Tag } from '../../components/platform/primitives';
import { FS, FW, R, SP } from '../../utils/platformTokens';
import { useBlockCopyPaste, noCopyPasteProps } from '../../utils/noCopyPaste';

/* ── Wordmark ── */
export const Wordmark: React.FC<{ size?: number }> = ({ size = 18 }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View
        style={{
          width: size + 8, height: size + 8, borderRadius: 6, backgroundColor: c.ink,
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Text style={{ color: c.paper, fontWeight: '800', fontSize: size * 0.62 }}>SK</Text>
      </View>
      <Text style={{ color: c.ink, fontWeight: '700', fontSize: size, letterSpacing: -0.3 }}>
        Platform
      </Text>
    </View>
  );
};

/* ── Lightweight dropdown menu (outside-press to close) ── */
const Menu: React.FC<{
  trigger: (open: () => void) => React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  width?: number;
  align?: 'left' | 'right';
}> = ({ trigger, children, width = 220, align = 'right' }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [open, setOpen] = useState(false);
  return (
    <View style={{ position: 'relative' }}>
      {trigger(() => setOpen(true))}
      {open && (
        <RNModal transparent visible animationType="fade" onRequestClose={() => setOpen(false)}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} />
          <View
            style={[
              styles.menu,
              {
                width,
                top: 52,
                [align]: 16,
                backgroundColor: c.card,
                borderColor: c.line,
              } as any,
            ]}
          >
            {children(() => setOpen(false))}
          </View>
        </RNModal>
      )}
    </View>
  );
};

export const MenuItem: React.FC<{
  icon?: IconName;
  children: React.ReactNode;
  onPress: () => void;
  danger?: boolean;
}> = ({ icon, children, onPress, danger }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10,
        paddingVertical: 8, borderRadius: R.r2, backgroundColor: hover ? c.paper2 : 'transparent',
      }}
    >
      {icon && <Icon name={icon} size={15} color={danger ? c.danger : c.ink} />}
      <Text style={{ color: danger ? c.danger : c.ink, fontSize: FS.md }}>{children}</Text>
    </Pressable>
  );
};

/* ── TopBar ── */
export const TopBar: React.FC = () => {
  const { theme, isDark, toggleTheme } = useTheme();
  const c = theme.colors;
  const { role, isPlatformAdmin, state } = usePlatform();
  const { route, goApp, signOut } = useShellNav();
  const inAdmin = route.area === 'admin';

  const products: { id: 'crm' | 'catalog' | 'voice'; label: string }[] = [
    { id: 'crm', label: 'CRM' },
    { id: 'catalog', label: 'Catalog' },
    { id: 'voice', label: 'Voice' },
  ];

  const NavBtn: React.FC<{ active: boolean; onPress: () => void; children: React.ReactNode }> = ({
    active, onPress, children,
  }) => (
    <Pressable
      onPress={onPress}
      style={{
        height: 26, paddingHorizontal: 10, borderRadius: R.r2, justifyContent: 'center',
        backgroundColor: active ? c.paper3 : 'transparent',
      }}
    >
      <Text style={{ fontSize: FS.sm, color: active ? c.ink : c.ink2, fontWeight: FW.medium }}>
        {children}
      </Text>
    </Pressable>
  );

  return (
    <View style={[styles.topbar, { backgroundColor: c.card, borderColor: c.line }]}>
      <Pressable onPress={() => goApp('tenant', 'home')}>
        <Wordmark size={18} />
      </Pressable>

      {/* workspace / area switcher */}
      <Menu
        align="left"
        width={252}
        trigger={(open) => (
          <Pressable
            onPress={open}
            style={[styles.wsBtn, { borderColor: c.line, backgroundColor: c.card }]}
          >
            <View
              style={{
                width: 18, height: 18, borderRadius: 4, alignItems: 'center', justifyContent: 'center',
                backgroundColor: inAdmin ? c.accent : c.ink,
              }}
            >
              <Text style={{ color: c.paper, fontSize: 10, fontWeight: '700' }}>{inAdmin ? 'SK' : 'A'}</Text>
            </View>
            <Text style={{ fontSize: FS.sm, color: c.ink, fontWeight: FW.medium }}>
              {inAdmin ? 'Platform' : state.org.name.split(' ')[0] + ' Retail'}
            </Text>
            <Icon name="chev-d" size={12} color={c.ink3} />
          </Pressable>
        )}
      >
        {(close) => (
          <>
            <Text style={[styles.menuLabel, { color: c.ink3 }]}>WORKSPACES</Text>
            <MenuItem icon={!inAdmin ? 'check' : 'home'} onPress={() => { goApp('tenant', 'home'); close(); }}>
              Acme Retail Pvt Ltd
            </MenuItem>
            {isPlatformAdmin && (
              <>
                <View style={[styles.divider, { backgroundColor: c.line }]} />
                <Text style={[styles.menuLabel, { color: c.ink3 }]}>STAFF</Text>
                <MenuItem icon={inAdmin ? 'check' : 'lock'} onPress={() => { goApp('admin', 'admin-overview'); close(); }}>
                  Platform Admin Console
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* product nav (tenant only) */}
      {!inAdmin ? (
        <View style={{ flexDirection: 'row', gap: 2, marginLeft: 4 }}>
          {products.map((p) => (
            <NavBtn key={p.id} active={route.area === p.id} onPress={() => goApp(p.id, p.id)}>
              {p.label}
            </NavBtn>
          ))}
          <NavBtn active={route.area === 'tenant'} onPress={() => goApp('tenant', 'home')}>
            Settings
          </NavBtn>
        </View>
      ) : (
        <View style={{ marginLeft: 4 }}>
          <Tag tone="accent" size="lg">Platform Admin</Tag>
        </View>
      )}

      <View style={{ flex: 1 }} />

      {/* theme toggle */}
      <Pressable onPress={toggleTheme} hitSlop={8} style={styles.iconBtn}>
        <Icon name={isDark ? 'sun' : 'moon'} size={16} color={c.ink2} />
      </Pressable>

      {/* one-click logout */}
      <Pressable
        onPress={signOut}
        hitSlop={8}
        accessibilityLabel="Sign out"
        style={[styles.logoutBtn, { borderColor: c.line }]}
      >
        <Icon name="logout" size={15} color={c.ink2} />
        <Text style={{ fontSize: FS.sm, color: c.ink2, fontWeight: FW.medium }}>Logout</Text>
      </Pressable>

      {/* user menu */}
      <Menu
        width={224}
        trigger={(open) => (
          <Pressable onPress={open}>
            <Avatar name={state.currentUser.name} size="sm" />
          </Pressable>
        )}
      >
        {(close) => (
          <>
            <View style={{ paddingHorizontal: 10, paddingVertical: 6 }}>
              <Text style={{ fontWeight: FW.semibold, fontSize: FS.md, color: c.ink }}>
                {state.currentUser.name}
              </Text>
              <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{state.currentUser.email}</Text>
              <View style={{ flexDirection: 'row', gap: 6, marginTop: 6 }}>
                <Tag tone="accent" size="lg">{role}</Tag>
                {isPlatformAdmin && <Tag tone="info" size="lg">Staff</Tag>}
              </View>
            </View>
            <View style={[styles.divider, { backgroundColor: c.line }]} />
            <MenuItem icon="user" onPress={() => { goApp('tenant', 'security'); close(); }}>Your profile</MenuItem>
            <MenuItem icon="lock" onPress={() => { goApp('tenant', 'security'); close(); }}>Security</MenuItem>
            {isPlatformAdmin && (
              <MenuItem icon="settings" onPress={() => { goApp('admin', 'admin-overview'); close(); }}>Admin Console</MenuItem>
            )}
            <View style={[styles.divider, { backgroundColor: c.line }]} />
            <MenuItem icon="logout" danger onPress={() => { close(); signOut(); }}>Sign out</MenuItem>
          </>
        )}
      </Menu>
    </View>
  );
};

/* ── SubNav (left, 220px) ── */
export const SubNav: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role } = usePlatform();
  const { route, goApp } = useShellNav();
  const inAdmin = route.area === 'admin';
  const items = inAdmin ? ADMIN_NAV : NAV.filter((n) => n.roles.includes(role));
  const heading = inAdmin ? 'Admin Console' : 'Settings';

  return (
    <View style={[styles.subnav, { borderColor: c.line }]}>
      <Text style={[styles.subnavHeading, { color: c.ink3 }]}>{heading.toUpperCase()}</Text>
      <View style={{ gap: 2 }}>
        {items.map((n) => {
          const active = route.screen === n.id;
          return (
            <Pressable
              key={n.id}
              onPress={() => goApp(route.area, n.id as any)}
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10,
                paddingVertical: 8, borderRadius: R.r2,
                backgroundColor: active ? c.accentSoft : 'transparent',
              }}
            >
              <Icon name={n.icon} size={16} color={active ? c.accentInk : c.ink2} />
              <Text
                style={{
                  fontSize: FS.md, color: active ? c.accentInk : c.ink2,
                  fontWeight: active ? FW.semibold : FW.regular,
                }}
              >
                {n.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

/* ── SectionSelect (mobile/tablet < 900px) ── */
export const SectionSelect: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role } = usePlatform();
  const { route, goApp } = useShellNav();
  const inAdmin = route.area === 'admin';
  const items = inAdmin ? ADMIN_NAV : NAV.filter((n) => n.roles.includes(role));

  // Cross-platform: a simple horizontal scroll of pill options (works on native too).
  return (
    <View style={{ padding: 12, borderBottomWidth: 1, borderColor: c.line }}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
        {items.map((n) => {
          const active = route.screen === n.id;
          return (
            <Pressable
              key={n.id}
              onPress={() => goApp(route.area, n.id as any)}
              style={{
                paddingHorizontal: 12, height: 34, borderRadius: R.r2, justifyContent: 'center',
                borderWidth: 1, borderColor: active ? c.accentLine : c.line,
                backgroundColor: active ? c.accentSoft : c.card,
              }}
            >
              <Text style={{ fontSize: FS.sm, color: active ? c.accentInk : c.ink2, fontWeight: FW.medium }}>
                {n.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

/* ── PermissionDenied ── */
export const PermissionDenied: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { goApp } = useShellNav();
  return (
    <View style={{ maxWidth: 460, alignSelf: 'center', marginTop: 80, alignItems: 'center', paddingHorizontal: 24 }}>
      <View
        style={{
          width: 56, height: 56, borderRadius: 28, backgroundColor: c.paper3,
          alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}
      >
        <Icon name="lock" size={26} color={c.ink3} />
      </View>
      <Text style={{ fontSize: FS.xxl, fontWeight: FW.semibold, color: c.ink, marginBottom: 8 }}>
        You don't have access
      </Text>
      <Text style={{ color: c.ink2, fontSize: FS.lg, textAlign: 'center', marginBottom: 20, lineHeight: 22 }}>
        This area is limited to administrators. Ask an admin in your workspace to grant access or make
        the change for you.
      </Text>
      <Pressable
        onPress={() => goApp('tenant', 'home')}
        style={{ height: 32, paddingHorizontal: 14, borderRadius: R.r2, borderWidth: 1, borderColor: c.line, justifyContent: 'center', backgroundColor: c.card }}
      >
        <Text style={{ color: c.ink, fontSize: FS.md, fontWeight: FW.medium }}>Back to Organization</Text>
      </Pressable>
    </View>
  );
};

/* ── SessionReauthModal ── */
export const SessionReauthModal: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { sessionLocked, unlockSession } = usePlatform();
  const [pw, setPw] = useState('');
  // Re-auth password field: no copy/cut/paste, same as the auth screens.
  const pwRef = useBlockCopyPaste<TextInput>(true);
  const [loading, setLoading] = useState(false);
  if (!sessionLocked) return null;
  const submit = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); setPw(''); unlockSession(); }, 600);
  };
  return (
    <RNModal transparent visible animationType="fade">
      <View style={styles.modalBackdrop}>
        <View style={[styles.modalSm, { backgroundColor: c.card }]}>
          <View style={[styles.modalHead, { borderColor: c.line }]}>
            <Text style={{ fontSize: FS.lg, fontWeight: FW.semibold, color: c.ink }}>Session expired</Text>
          </View>
          <View style={{ padding: 18 }}>
            <Text style={{ color: c.ink2, fontSize: FS.md, marginBottom: 16, lineHeight: 20 }}>
              For your security you were signed out after 12 hours of inactivity. Sign in to continue —
              your unsaved work is preserved.
            </Text>
            <Text style={{ fontSize: FS.sm, color: c.ink2, marginBottom: 4 }}>Password</Text>
            <TextInput
              ref={pwRef}
              value={pw}
              onChangeText={setPw}
              secureTextEntry
              {...noCopyPasteProps(true)}
              placeholder="Your password"
              placeholderTextColor={c.ink4}
              style={[styles.input, { borderColor: c.line, color: c.ink, backgroundColor: c.card }]}
            />
            <Pressable
              onPress={submit}
              style={{ marginTop: 16, height: 38, borderRadius: R.r2, backgroundColor: c.accent, alignItems: 'center', justifyContent: 'center' }}
            >
              <Text style={{ color: '#fff', fontWeight: FW.semibold, fontSize: FS.md }}>
                {loading ? 'Signing in…' : 'Sign in & continue'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  topbar: {
    height: 56, borderBottomWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 14,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' ? ({ position: 'sticky', top: 0, zIndex: 20 } as any) : {}),
  },
  wsBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8, height: 26, paddingHorizontal: 10,
    borderRadius: R.r2, borderWidth: 1,
  },
  iconBtn: { width: 32, height: 32, borderRadius: R.r2, alignItems: 'center', justifyContent: 'center' },
  logoutBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 30, paddingHorizontal: 10, borderRadius: R.r2, borderWidth: 1 },
  menu: {
    position: 'absolute', borderWidth: 1, borderRadius: R.r3, padding: 6,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 32px -12px rgba(16,24,40,0.30)' } as any) : { elevation: 8 }),
  },
  menuLabel: { fontSize: FS.xs, paddingHorizontal: 10, paddingVertical: 6, letterSpacing: 0.5 },
  divider: { height: 1, marginVertical: 6 },
  subnav: { width: 220, flexShrink: 0, paddingVertical: 20, paddingHorizontal: 12, borderRightWidth: 1 },
  subnavHeading: { fontSize: FS.xs, letterSpacing: 0.6, paddingHorizontal: 10, paddingBottom: 10, fontWeight: FW.semibold },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modalSm: { width: 380, maxWidth: '100%', borderRadius: R.r4, overflow: 'hidden' },
  modalHead: { paddingHorizontal: 18, paddingVertical: 14, borderBottomWidth: 1 },
  input: { height: 34, borderWidth: 1, borderRadius: R.r2, paddingHorizontal: 12, fontSize: FS.md, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) },
});
